<template>
  <div class="recommended-products">
    <Heading
      tag="h2"
      size="md"
      class="recommended-products__heading"
      font-weight="regular"
      spaces-bottom
    >
      Beliebte Produkte
    </Heading>
    <DraggableScroller
      ref="draggableScrollbar"
      has-pagination
      pagination-label="Beliebte Produkte Paginierung"
      show-single-active-pagination
    >
      <ProductOfferTileSMContainer
        :items="getOffersByGroup(promotionsWithOffers, 'first')"
        tile-type="small"
        class="recommended-products__list-entry"
        heading-tag="h3"
        no-delay-animation
      />
    </DraggableScroller>
  </div>
</template>

<script lang="ts" setup>
import { defineComponent, ref } from 'vue';
import { getFirstOfferAlternatives, type TransformedAlternative } from '@i22/rocket';
import { Heading } from '@i22/td-longtail-vue-components';
import ProductOfferTileSMContainer from '~/components/product-offer-tile-s-m-container.vue';
import DraggableScroller from '@/components/draggable-scroller.vue';
import {
  loadOffersForPromotions,
  getOffersByGroup,
  type Promotion,
  type PromotionOffer,
} from '@/lib/offers-by-group';
import { getBrand } from '~/lib/brand';

defineComponent({ name: 'RecommendedProducts' });

interface Recommended extends Promotion {
  teaserTitle?: string;
  description: string;
  tileGroups: string[];
  showLocalTitle?: boolean;
}
const recommendedProducts = ref<Recommended[]>([
  {
    articleNumber: '4262362473465', // Edurino Schulstartbundle
    description: '',
    showLocalTitle: true,
    tileGroups: ['first'],
  },
  {
    articleNumber: '5706751072239', // Denver TIO-80105KBUP Kids Tablet
    description: '',
    showLocalTitle: true,
    tileGroups: ['first'],
  },
  {
    articleNumber: '4251281683932BU', // Sinus 207 + Sinus 207 Pack
    description: '',
    showLocalTitle: true,
    tileGroups: ['first'],
  },
  {
    articleNumber: '8718819375890', // Fairbuds Wireless Earbuds
    description: '',
    showLocalTitle: true,
    tileGroups: ['first'],
  },
  {
    articleNumber: '6934177783494', // XIAOMI Smart Band 7 EU
    description: '',
    showLocalTitle: true,
    tileGroups: ['first'],
  },
  {
    articleNumber: '0840023272271', // Motorola Moto Tag
    description: '',
    showLocalTitle: true,
    tileGroups: ['first'],
  },
  {
    articleNumber: '4260195392229', // Eve Aqua
    description: '',
    showLocalTitle: true,
    tileGroups: ['first'],
  },
]);

const draggableScrollbar = ref(null);

// eslint-disable-next-line no-undef
const { data: promotionsWithOffers } = useLazyAsyncData<PromotionOffer['offerBySlugs']>(
  'recommended-products',
  async () => {
    const data = await loadOffersForPromotions(recommendedProducts.value, false, false);

    setTimeout(() => {
      // Update scroller for new pages
      draggableScrollbar.value?.updateScroller();
    }, 1);

    return data;
  }
);

/*
useFetch(async () => {
  try {
    promotionsWithOffers.value = await loadOffersForPromotions(
      recommendedProducts.value,
      false,
      false
    );

    setTimeout(() => {
      // Update scroller for new pages
      draggableScrollbar.value?.updateScroller();
    }, 1);
  } catch (e: any) {
    console.error(e);
  }
}); */

const getBrandLogo = (promotion: PromotionOffer) => getBrand(promotion.offer);

function getColorVariants(
  promotion: PromotionOffer
): TransformedAlternative<NonNullable<PromotionOffer['offerBySlugs']>> | undefined {
  if (!promotion?.offer) return undefined;
  return getFirstOfferAlternatives(promotion.offer.productAlternatives);
}
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.recommended-products {
  &__heading {
    padding-inline: $grid-base;
  }

  :deep(.draggable-scroller__container) {
    list-style-type: none;
    margin-bottom: $grid-base;

    @include mq(false, $mq-sm - 1) {
      scroll-snap-type: x mandatory;
    }
  }

  :deep(.grid-container) {
    max-width: none;
    width: auto;
    padding: 0;
  }

  :deep(.pot-sm-container__row) {
    flex-wrap: nowrap;
    margin-inline: 0;
  }

  :deep(.pot-sm-container__column) {
    padding-inline: $grid-base;
    flex: 0 0 100%;

    @include mq($mq-sm) {
      flex-basis: 50%;
    }

    @include mq($mq-md) {
      flex-basis: 33.3333%;
    }

    @include mq($mq-lg) {
      flex-basis: 25%;
    }

    @include mq(false, $mq-sm - 1) {
      scroll-snap-align: start;
    }
  }

  &__hover-image {
    transition: transform 0.3s;
    transform: scale(1.05);
  }

  :deep(.product-offer-tile) {
    .product-offer-tile {
      &__product-image-container {
        justify-content: center;
        img {
          width: 100%;
          aspect-ratio: 1 / 1;
        }
      }

      &__product-image-link {
        width: 100%;

        > a {
          display: block;
        }
      }

      &__image {
        img {
          width: 100%;
          aspect-ratio: 1 / 1;
        }
      }
    }
  }
}
</style>
